import React from "react";
import { COMPANY } from "../data/site";
import { Phone, Mail, MapPin, MessageCircle, Crown, User, Send } from "lucide-react";
import useReveal from "../hooks/useReveal";

const Contact = () => {
  const main = COMPANY.contacts[0];
  const second = COMPANY.contacts[1];
  const [ref, inView] = useReveal();

  return (
    <section
      id="contact"
      ref={ref}
      className={`relative py-28 bg-[#fffaf3] overflow-hidden reveal ${inView ? "in-view" : ""}`}
      data-testid="contact-section"
    >
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-[10px] font-extrabold tracking-[0.25em] uppercase font-mono mb-5">
            <Send className="w-3 h-3" /> Get in touch
          </div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#1a0a05] leading-[0.95] tracking-tight">
            Contact <span className="text-heat brush">Details</span>.
          </h2>
          <p className="text-[#3a2418] mt-5 max-w-2xl mx-auto">
            Call, message or drop by our Delhi facility. Our team will help you specify the right heater for your application.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <ContactCard
            testId="contact-main"
            badge={{ icon: <Crown className="w-3 h-3" />, label: "Main Contact" }}
            icon={<Phone className="w-5 h-5 text-white" />}
            title={main.name}
            subtitle={main.role}
            primary={main.phone}
            primaryHref={`tel:${main.phone}`}
            actions={[
              { label: "Call", href: `tel:${main.phone}`, kind: "primary", icon: <Phone className="w-3.5 h-3.5" /> },
              {
                label: "WhatsApp",
                href: `https://wa.me/${main.whatsapp}?text=${encodeURIComponent(
                  "Hello, I would like to enquire about your heaters."
                )}`,
                target: "_blank",
                kind: "whatsapp",
                icon: <MessageCircle className="w-3.5 h-3.5" />,
              },
            ]}
          />

          <ContactCard
            testId="contact-secondary"
            badge={{ icon: <User className="w-3 h-3" />, label: second.role }}
            icon={<Phone className="w-5 h-5 text-white" />}
            title={second.name}
            subtitle="Founder"
            primary={second.phone}
            primaryHref={`tel:${second.phone}`}
            actions={[
              { label: "Call", href: `tel:${second.phone}`, kind: "dark", icon: <Phone className="w-3.5 h-3.5" /> },
              {
                label: "WhatsApp",
                href: `https://wa.me/${second.whatsapp}`,
                target: "_blank",
                kind: "whatsapp",
                icon: <MessageCircle className="w-3.5 h-3.5" />,
              },
            ]}
          />

          <ContactCard
            testId="contact-email"
            badge={{ icon: <Mail className="w-3 h-3" />, label: "Drop a mail" }}
            icon={<Mail className="w-5 h-5 text-white" />}
            title="Email"
            subtitle="Replies within 2h"
            primary={COMPANY.emails[0]}
            primaryHref={`mailto:${COMPANY.emails[0]}`}
            actions={COMPANY.emails.slice(1, 2).map((e) => ({
              label: e,
              href: `mailto:${e}`,
              kind: "ghost",
              icon: <Mail className="w-3.5 h-3.5" />,
              full: true,
            }))}
          />

          <ContactCard
            testId="contact-address-card"
            badge={{ icon: <MapPin className="w-3 h-3" />, label: "Visit Us" }}
            icon={<MapPin className="w-5 h-5 text-white" />}
            title="Our Factory"
            subtitle="Delhi, India"
            primary={COMPANY.address}
            actions={[
              {
                label: "Open Maps",
                href: "https://www.google.com/maps/search/?api=1&query=Samay+Pur+Extension+Industrial+Area+Delhi+110042",
                target: "_blank",
                kind: "primary",
                full: true,
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
};

const ContactCard = ({ testId, badge, icon, title, subtitle, primary, primaryHref, actions = [] }) => (
  <article
    data-testid={testId}
    className="group relative bg-white rounded-[1.6rem] p-6 border border-orange-100 flex flex-col h-full lift overflow-hidden"
  >
    <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-orange-300/15 blur-2xl group-hover:bg-orange-400/30 transition-colors" />
    <div className="relative">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-extrabold tracking-[0.18em] uppercase mb-5 w-fit font-mono">
        {badge.icon} {badge.label}
      </div>
      <div className="w-11 h-11 rounded-2xl heat-gradient grid place-items-center mb-4 shadow-lg shadow-red-500/30">
        {icon}
      </div>
      <div className="font-display font-extrabold text-xl text-[#1a0a05] leading-tight tracking-tight">{title}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold mt-1 font-mono">{subtitle}</div>

      {primary &&
        (primaryHref ? (
          <a
            href={primaryHref}
            className="block mt-4 text-[#3a1106] font-semibold text-sm leading-snug hover:text-orange-600 break-all link-underline"
          >
            {primary}
          </a>
        ) : (
          <p className="block mt-4 text-[#3a1106] font-semibold text-sm leading-snug">{primary}</p>
        ))}
    </div>

    <div className="relative mt-auto pt-5 flex flex-wrap gap-2">
      {actions.map((a, i) => (
        <a
          key={i}
          href={a.href}
          target={a.target}
          rel={a.target ? "noreferrer" : undefined}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2.5 rounded-full transition-transform hover:scale-[1.02] ${
            BTN_KIND[a.kind] || BTN_KIND.primary
          }`}
        >
          {a.icon} {a.label}
        </a>
      ))}
    </div>
  </article>
);

const BTN_KIND = {
  primary: "heat-gradient text-white shadow-lg shadow-red-500/25",
  dark: "bg-[#1a0a05] hover:bg-[#3a1106] text-white",
  whatsapp: "bg-green-600 hover:bg-green-700 text-white",
  ghost: "bg-orange-50 hover:bg-orange-100 text-[#3a1106] border border-orange-200",
};

export default Contact;
